import {
  Flex,
  HStack,
  Spinner,
  Stack,
  StackSeparator,
  Text,
  VStack,
  Table,
  Switch,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { t } from "i18next";
import { getAttributeValue } from "../coding/utils/attribute-value";
import { DemonstratorInput } from "./components/DemonstratorInput";
import {
  AreaType,
  BicycleAndLVFlowType,
  CurvatureType,
  AdequateType,
  FacilityType,
  FacilityWidth,
  FlowDirectionType,
  GradeType,
  HeavyVehicleFlowType,
  IntersectionApproachType,
  NumberOfLanesType,
  PedestrianFlowType,
  PresenceType,
  ProportionOfCargoBikesAndMopedsType,
  SpeedAverageType,
  SpeedDifferentialType,
  CraAttribute,
  CraAttributes,
  CraAttributeName,
  CodingAttributes,
  DistanceUnit,
} from "@keita/lanepatrol-sdk";
import { CraAttributeUtils } from "../coding/utils/attribute-utils";
import { CraSimulatedResult } from "../project/models/CraForecastScenario";
import { OsaNodeCodingApi } from "../coding/services/OsaNodeCodingApi";
import {
  AppHeader,
  AppHeaderHeight,
} from "../shared/views/AppHeader/AppHeader";
import { ViewScoreSmall } from "../coding/views/ViewScoreSmall";
import { NodeTreatments } from "../pages/map/views/DetailBar/node/NodeTreatments";

const DemonstratorPageBase: React.FC = () => {
  const [codingEntryAttributes, setCodingAttributes] =
    useState(defaultAttributes);
  const [result, setResult] = useState<CraSimulatedResult>();
  const [loadingScore, setLoadingScore] = useState(true);
  const [distanceUnit, setDistanceUnit] = useState<DistanceUnit>(
    DistanceUnit.Metric,
  );

  const applyChange = async (attributeName: string, value: any) => {
    const attributeNames = attributeName.split(".");
    const entryKey = attributeNames[0] as keyof CodingAttributes;
    const subEntryKey = attributeNames[1];
    const valueObject = subEntryKey
      ? {
          ...(codingEntryAttributes[entryKey] as object),
          [subEntryKey]: value,
        }
      : value;
    const newOsaNodeCoding: CodingAttributes = {
      ...codingEntryAttributes,
      [entryKey]: valueObject,
    };
    setCodingAttributes(newOsaNodeCoding);
  };

  React.useEffect(() => {
    (async () => {
      setLoadingScore(true);

      await OsaNodeCodingApi.simulateResults({
        distanceUnit: distanceUnit,
        attributes: [codingEntryAttributes],
      })
        .then((response) => {
          setResult(response.data[0]);
        })
        .catch(() => {
          setResult(undefined);
        })
        .finally(() => {
          setLoadingScore(false);
        });
    })();
  }, [codingEntryAttributes, distanceUnit]);

  return (
    <Flex flexDir="column" maxHeight="100vh" flexGrow={1}>
      <AppHeader />
      <AppHeaderHeight />
      <HStack
        flexGrow={1}
        alignItems="stretch"
        gap={0}
        separator={<StackSeparator />}
        overflowY="auto"
      >
        <Stack flexGrow={1} overflowY="auto">
          <Flex alignItems="center" px={4} pt={4} pb={2} gap={3}>
            <Text fontSize="lg" fontWeight="semibold" flex={1}>
              {t("demonstrator:header.coding")}
            </Text>
            <Text>{DistanceUnit.Metric}</Text>
            <Switch.Root
              checked={distanceUnit === DistanceUnit.Imperial}
              onCheckedChange={(details) =>
                setDistanceUnit(
                  details.checked ? DistanceUnit.Imperial : DistanceUnit.Metric,
                )
              }
              size="sm"
              colorPalette="primary"
            >
              <Switch.HiddenInput />
              <Switch.Control />
              <Switch.Label>{DistanceUnit.Imperial}</Switch.Label>
            </Switch.Root>
          </Flex>

          <Table.Root striped size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader pl={4}>
                  {t("demonstrator:header.attribute")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("demonstrator:header.value")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {CraAttributes.all.map((attribute) => {
                if (attribute.name === CraAttributeName.Comment) return null;
                return (
                  <Attribute
                    key={attribute.name}
                    codingEntryAttributes={codingEntryAttributes}
                    attribute={attribute}
                    applyChange={applyChange}
                    label={CraAttributeUtils.getLabel(attribute.name)}
                  />
                );
              })}
            </Table.Body>
          </Table.Root>
        </Stack>

        <VStack w="360px" flexShrink={0} padding={4} gap={4}>
          <Text fontSize="lg" fontWeight="semibold">
            {t("demonstrator:header.score")}
          </Text>
          {loadingScore ? (
            <Spinner my={10} borderWidth="3px" size="lg" color="primary.500" />
          ) : (
            !!result && (
              <>
                <ViewScoreSmall score={result.score} />
                <Text>{t("map:treatments.title")}</Text>
                <NodeTreatments treatments={result.treatments} />
              </>
            )
          )}
        </VStack>
      </HStack>
    </Flex>
  );
};

export const DemonstratorPage = observer(DemonstratorPageBase);

interface AttributeProps {
  attribute: CraAttribute;
  codingEntryAttributes: CodingAttributes;
  label: string;
  applyChange: (attributeName: string, value: any) => void;
}

const Attribute: React.FC<AttributeProps> = ({
  attribute,
  label,
  codingEntryAttributes,
  applyChange,
}) => {
  return (
    <Table.Row>
      <Table.Cell pl={4} fontSize="sm">
        {label}
      </Table.Cell>
      <Table.Cell p={0}>
        <DemonstratorInput
          applyChange={applyChange}
          attribute={attribute}
          value={getAttributeValue(attribute.name, codingEntryAttributes)}
        />
      </Table.Cell>
    </Table.Row>
  );
};

const defaultAttributes: CodingAttributes = {
  comment: null,
  areaType: AreaType.InnerUrban,
  facilityType: FacilityType.MultiUsePath,
  facilityAccess: AdequateType.Adequate,
  looseOrSlipperySurface: PresenceType.NotPresent,
  tramOrTrainRails: PresenceType.NotPresent,
  majorSurfaceDeformationOrDrainOpening: PresenceType.NotPresent,
  fixedObstacleOnFacility: PresenceType.NotPresent,
  nonFixedObstacleOnFacility: PresenceType.NotPresent,
  lineOfSight: AdequateType.Adequate,
  delineation: PresenceType.NotPresent,
  lightSegregation: PresenceType.NotPresent,
  facilityWidthPerDirection: FacilityWidth.Narrow,
  flowDirection: FlowDirectionType.OneWay,
  widthRestriction: PresenceType.NotPresent,
  adjacentFeatures0to1m: {
    roadLane: PresenceType.NotPresent,
    vehicleParking: PresenceType.NotPresent,
    severeHazard: PresenceType.NotPresent,
    objectOrLevelChange: PresenceType.NotPresent,
    sidewalk: PresenceType.NotPresent,
  },
  adjacentFeatures1to3m: {
    roadLane: PresenceType.NotPresent,
    vehicleParking: PresenceType.NotPresent,
    severeHazard: PresenceType.NotPresent,
    objectOrLevelChange: PresenceType.NotPresent,
    sidewalk: PresenceType.NotPresent,
  },
  grade: GradeType.LessThan5degrees,
  curvature: CurvatureType.NoSharpTurn,
  streetLighting: PresenceType.NotPresent,
  pedestrianCrossing: PresenceType.NotPresent,
  intersectionBicycleFacility: PresenceType.NotPresent,
  intersectionApproach: IntersectionApproachType.SeparateOrNA,
  intersectionOrRoadCrossing: PresenceType.NotPresent,
  crossingFacility: PresenceType.NotPresent,
  numberOfLanesAdjacentRoad: NumberOfLanesType.MoreThanOnePerDirection,
  numberOfLanesIntersectingRoad: NumberOfLanesType.MoreThanOnePerDirection,
  propertyAccess: PresenceType.NotPresent,
  peakPedestrianFlowAlongOrAcrossFacility: PedestrianFlowType.Low,
  peakBicycleAndLVTrafficFlow: BicycleAndLVFlowType.Low,
  observedProportionOfCargoBikesAndMopeds:
    ProportionOfCargoBikesAndMopedsType.ModerateToHigh,
  bicycleAndLVSpeedAverage: SpeedAverageType.LessThan20kmh,
  bicycleAndLVSpeedDifferential: SpeedDifferentialType.LessThan10kmh,
  roadAADT: 0,
  heavyVehicleFlow: HeavyVehicleFlowType.LowOrRestrictedAccess,
  roadSpeedLimit: 0,
  roadOperatingSpeed: 0,
};
